import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { User } from "@/domain/user/models/user-model";

import { UserSelectDropdownItem } from "./user-select-dropdown-item";

export interface UserSelectDropdownProps extends PropsWithChildren {
  activeUsers: User[];
  asChild?: boolean;
  selected?: {
    id: string;
    username?: string | null;
  } | null;
  side?: "top" | "right" | "bottom" | "left";
  onSelect?: (user: { id: string } | null) => void;
  isReadOnly?: boolean;
}

export const UserSelectDropdown: FC<UserSelectDropdownProps> = ({
  activeUsers,
  children,
  side,
  asChild,
  onSelect,
  isReadOnly,
  ...props
}) => {
  const { t } = useTranslation("common");
  const [selectedUser, setSelectedUser] = useState(props.selected ? props.selected : null);

  useEffect(() => {
    if (props.selected) {
      setSelectedUser(props.selected);
    }
  }, [props.selected]);

  // for faster retrieval
  const userMap = useMemo(() => {
    return new Map(activeUsers.map((obj) => [obj.id, obj]));
  }, [activeUsers]);

  const comboboxOptions = useMemo(() => {
    const userOptions = activeUsers
      .slice()
      .sort((a, b) => {
        if (!a || !b) {
          return 0;
        }
        const _a = a.username ?? "";
        const _b = b.username ?? "";

        return _a.localeCompare(_b);
      })
      .flatMap((u) => {
        if (!u) {
          return [];
        }
        return {
          id: u.id,
          value: `${u?.username ?? ""} ; ${u?.fullname ?? ""} ; ${u?.email ?? ""}`,
          original: u,
        };
      });
    return [{ id: "-1", value: t("user.noResponsible"), original: null }, ...userOptions];
  }, [activeUsers, t]);

  const renderItem = (status: ComboboxOption<User>): ReactElement => {
    const user = userMap.get(status.id) ?? undefined;
    const selected = (status.id === "-1" && selectedUser === null) || selectedUser?.id === status.id;
    return <UserSelectDropdownItem user={user} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<User> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === "-1") {
      setSelectedUser(null);
      if (onSelect) {
        onSelect(null);
      }
      return;
    }

    setSelectedUser(selectedOption.original);
    if (onSelect) {
      onSelect(selectedOption.original);
    }
  };

  if (!comboboxOptions) {
    return <></>;
  }

  if (isReadOnly) {
    if (!selectedUser) {
      return <div className="pointer-events-none">{children}</div>;
    }

    return (
      <div title={selectedUser?.username ?? ""} className="flex items-center gap-2">
        <div className="pointer-events-none">{children}</div>
      </div>
    );
  }

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      side={side}
      isReadOnly={isReadOnly}
    >
      {children}
    </Combobox>
  );
};
